import { Injectable } from '@angular/core';
import * as uuid from 'uuid';
import { AddressCaptureSessionService } from '../address-capture-session.service';

/**
 * OrderFlowSessionService
 *
 * Correlação do funil do pedido: menu → item → carrinho → entrega → pagamento
 * → sucesso.
 *
 * Por que não estender o AddressCaptureSessionService: ele nasce na abertura do
 * modal de endereço e é limpo no commit — a semântica dele pertence ao dataset
 * de geocode, que está vivo em produção. Este serviço o INJETA (dependência
 * unidirecional) e reusa o `device_id`, para os dois datasets continuarem
 * cruzáveis no Athena.
 */
const ATTEMPT_KEY = 'order_flow_attempt';

@Injectable({ providedIn: 'root' })
export class OrderFlowSessionService {
  private _attemptId = '';
  private _seq = 0;
  private _cartId = '';

  constructor(private addressSession: AddressCaptureSessionService) {
    this.restore();
  }

  /**
   * Re-hidrata do sessionStorage. Existe por causa dos pagamentos que SAEM do
   * app e voltam (PIX/Asaas): sem isto, o retorno começaria um attempt novo e o
   * funil apareceria partido em dois no Athena.
   *
   * sessionStorage, não localStorage: o attempt morre com a aba. Em
   * localStorage vazaria entre abas e entre dias, colando funis de pedidos
   * diferentes sob o mesmo id.
   */
  private restore(): void {
    try {
      const raw = sessionStorage.getItem(ATTEMPT_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      this._attemptId = saved.attemptId || '';
      this._seq = Number(saved.seq) || 0;
      this._cartId = saved.cartId || '';
    } catch (e) {
      // storage indisponível (modo privado/iframe): sessão em memória serve.
    }
  }

  private persist(): void {
    try {
      sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify({
        attemptId: this._attemptId, seq: this._seq, cartId: this._cartId,
      }));
    } catch (e) {
      // idem
    }
  }

  /** correlation_id da tentativa de pedido atual ('' se nenhuma ativa). */
  get attemptId(): string { return this._attemptId; }

  /** Identidade do carrinho atual — sobrevive à navegação, morre no clearCart. */
  get cartId(): string { return this._cartId; }

  /** UUID estável por dispositivo. Delegado: os dois datasets usam o MESMO. */
  get deviceId(): string { return this.addressSession.deviceId; }

  /** correlation_id da captura de endereço — o elo com o dataset de geocode. */
  get addressCorrelationId(): string { return this.addressSession.id; }

  /** Fluxo de endereço da sessão ('legado' | 'mpboy' | 'highprecision'). */
  get addressFlow(): string { return this.addressSession.flow; }

  /**
   * Garante um attempt SEM recriar se já existe (idempotente).
   *
   * Idempotência importa: o primeiro evento do funil pode ser o menu, mas
   * também pode ser um deep-link direto no carrinho ou o retorno de um
   * pagamento. Recriar aqui partiria o funil — o mesmo motivo pelo qual o
   * `AddressCaptureSessionService.ensureId` existe (eventos-âncora saíam órfãos
   * no caminho de endereço pré-preenchido).
   */
  ensureAttempt(): string {
    if (!this._attemptId) {
      this._attemptId = uuid.v4();
      this._seq = 0;
      this.persist();
    }
    return this._attemptId;
  }

  /**
   * Próximo número de sequência do funil.
   *
   * NÃO é opcional: o `ts` do schema tem granularidade de SEGUNDO (formato
   * Hive), e o app recalcula total várias vezes na mesma centena de
   * milissegundos. Sem `funnel_seq`, esses eventos ficam inordenáveis e a
   * pergunta "onde o número mudou?" não tem resposta.
   */
  nextSeq(): number {
    this.ensureAttempt();
    this._seq++;
    this.persist();
    return this._seq;
  }

  /** Abre a identidade do carrinho no primeiro item, se ainda não houver. */
  ensureCart(): string {
    if (!this._cartId) {
      this._cartId = uuid.v4();
      this.persist();
    }
    return this._cartId;
  }

  /** Fim do funil: pedido enviado, ou carrinho esvaziado. */
  clear(): void {
    this._attemptId = '';
    this._seq = 0;
    this._cartId = '';
    try {
      sessionStorage.removeItem(ATTEMPT_KEY);
    } catch (e) {
      // idem
    }
  }
}
